import React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TalentSelector } from '@/components/common/TalentSelector';
import type { OppmoteFormData } from '../hooks/useOppmoteForm';
import { OppmoteTimeInput } from './OppmoteTimeInput';

interface OppmoteDialogProps {
  open: boolean;
  editingId: number | null;
  formData: OppmoteFormData;
  formError: string | null;
  onClose: () => void;
  onFormDataChange: (data: Partial<OppmoteFormData>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const OppmoteDialog: React.FC<OppmoteDialogProps> = ({
  open,
  editingId,
  formData,
  formError,
  onClose,
  onFormDataChange,
  onSubmit,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {editingId ? 'Rediger oppmøtetid' : 'Legg til oppmøtetid'}
          </DialogTitle>
          <DialogDescription>
            Angi navn og tidspunkt for oppmøte
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 flex flex-col flex-1 min-h-0">
          <div>
            <Label htmlFor="navn">Navn *</Label>
            <Input
              id="navn"
              value={formData.navn}
              onChange={(e) => onFormDataChange({ navn: e.target.value })}
              placeholder="F.eks. Crew oppmøte, Artist oppmøte"
              required
            />
          </div>

          <div>
            <Label htmlFor="beskrivelse">Beskrivelse</Label>
            <Textarea
              id="beskrivelse"
              value={formData.beskrivelse}
              onChange={(e) => onFormDataChange({ beskrivelse: e.target.value })}
              placeholder="Valgfri beskrivelse..."
              rows={2}
            />
          </div>

          <OppmoteTimeInput
            formData={formData}
            onFormDataChange={onFormDataChange}
          />

          <div className="flex flex-col flex-1 min-h-0">
            <Label>For hvem gjelder oppmøtetiden?</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Velg talenter eller talentgrupper som skal ha denne oppmøtetiden. Grupper lagres direkte og vises med "(gruppe)". Hvis ingen er valgt, gjelder oppmøtetiden for alle.
            </p>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <TalentSelector
                selectedTalents={formData.selectedTalents}
                onTalentsChange={(talents) =>
                  onFormDataChange({ selectedTalents: talents })
                }
                allowGroups={true}
              />
            </div>
          </div>

          {formError && (
            <div className="flex items-center gap-2 text-destructive text-sm flex-shrink-0">
              <AlertCircle className="h-4 w-4" />
              {formError}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit">
              {editingId ? 'Lagre endringer' : 'Legg til'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

